import React, { useState, useEffect, useRef } from 'react';
import { uploadParticipant, getParticipants } from './api';
import { deleteParticipant } from './api';
import { getCurrentStage, nextStage } from './api';
import { prevStage } from './api';
import { clearSession, verifySession } from './api';
import { Tabs, Tab, Box, Container, Paper, Card, CardContent, CardMedia, Typography, Button, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';


interface AdminPageProps {
  session: string;
}

const ROLES = ['Table Topic', 'Speaker', 'Evaluator'];

const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
};

const loadImageElement = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image preview'));
    img.src = src;
  });
};

const getExifOrientation = async (file: File): Promise<number> => {
  const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg' || /\.jpe?g$/i.test(file.name);
  if (!isJpeg) {
    return 1;
  }

  const buffer = await file.slice(0, 128 * 1024).arrayBuffer();
  const view = new DataView(buffer);

  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
    return 1;
  }

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffda || marker === 0xffd9) {
      break;
    }

    if (offset + 2 > view.byteLength) {
      break;
    }

    const segmentLength = view.getUint16(offset, false);
    if (segmentLength < 2 || offset + segmentLength > view.byteLength) {
      break;
    }

    if (marker === 0xffe1 && segmentLength >= 10) {
      const exifStart = offset + 2;
      if (view.getUint32(exifStart, false) !== 0x45786966) {
        return 1;
      }

      const tiffOffset = exifStart + 6;
      const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
      const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian);
      const ifdStart = tiffOffset + firstIfdOffset;

      if (ifdStart + 2 > view.byteLength) {
        return 1;
      }

      const entryCount = view.getUint16(ifdStart, littleEndian);
      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = ifdStart + 2 + (index * 12);
        if (entryOffset + 12 > view.byteLength) {
          return 1;
        }

        if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
          return view.getUint16(entryOffset + 8, littleEndian);
        }
      }
    }

    offset += segmentLength;
  }

  return 1;
};

const drawImageWithOrientation = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  orientation: number,
  additionalRotation = 0
) => {
  const swapsDimensions = orientation >= 5 && orientation <= 8;
  const sourceWidth = img.naturalWidth || img.width;
  const sourceHeight = img.naturalHeight || img.height;

  canvas.width = swapsDimensions ? sourceHeight : sourceWidth;
  canvas.height = swapsDimensions ? sourceWidth : sourceHeight;
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, sourceWidth, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, sourceWidth, sourceHeight);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, sourceHeight);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, canvas.width, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, canvas.width, sourceHeight);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, canvas.height);
      break;
    default:
      break;
  }

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  if (additionalRotation !== 0) {
    const rotatedCanvas = document.createElement('canvas');
    const rotatedCtx = rotatedCanvas.getContext('2d');
    if (!rotatedCtx) {
      return;
    }

    if (additionalRotation === 90 || additionalRotation === 270) {
      rotatedCanvas.width = canvas.height;
      rotatedCanvas.height = canvas.width;
    } else {
      rotatedCanvas.width = canvas.width;
      rotatedCanvas.height = canvas.height;
    }

    rotatedCtx.fillStyle = '#fff';
    rotatedCtx.fillRect(0, 0, rotatedCanvas.width, rotatedCanvas.height);
    rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
    rotatedCtx.rotate((additionalRotation * Math.PI) / 180);
    rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

    canvas.width = rotatedCanvas.width;
    canvas.height = rotatedCanvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(rotatedCanvas, 0, 0);
  }
};

const createNormalizedPreview = async (file: File): Promise<string> => {
  const orientation = await getExifOrientation(file);
  const sourceUrl = await readFileAsDataUrl(file);

  if (orientation === 1) {
    return sourceUrl;
  }

  const img = await loadImageElement(sourceUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return sourceUrl;
  }

  drawImageWithOrientation(canvas, ctx, img, orientation);
  return canvas.toDataURL(file.type || 'image/jpeg', 0.92);
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

const getTouchDistance = (a: { x: number; y: number }, b: { x: number; y: number }): number => {
  return Math.hypot(b.x - a.x, b.y - a.y);
};

const getTouchMidpoint = (a: { x: number; y: number }, b: { x: number; y: number }): { x: number; y: number } => {
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2
  };
};

const renderSquareCanvas = async (
  sourceUrl: string,
  rotation: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
  outputSize: number
): Promise<HTMLCanvasElement> => {
  const sourceImage = await loadImageElement(sourceUrl);

  const rotatedCanvas = document.createElement('canvas');
  const rotatedCtx = rotatedCanvas.getContext('2d');
  if (!rotatedCtx) {
    throw new Error('Failed to initialize rotated canvas');
  }

  // sourceUrl is already EXIF-normalized.
  drawImageWithOrientation(rotatedCanvas, rotatedCtx, sourceImage, 1, rotation);

  const width = rotatedCanvas.width;
  const height = rotatedCanvas.height;
  const minSide = Math.min(width, height);
  const safeZoom = clamp(zoom, 1, 3);
  const cropSize = Math.max(1, minSide / safeZoom);
  const maxOffsetX = Math.max(0, (width - cropSize) / 2);
  const maxOffsetY = Math.max(0, (height - cropSize) / 2);

  const centerX = (width / 2) + ((offsetX / 100) * maxOffsetX);
  const centerY = (height / 2) + ((offsetY / 100) * maxOffsetY);
  const sourceX = clamp(centerX - (cropSize / 2), 0, width - cropSize);
  const sourceY = clamp(centerY - (cropSize / 2), 0, height - cropSize);

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = outputSize;
  outputCanvas.height = outputSize;

  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) {
    throw new Error('Failed to initialize output canvas');
  }

  outputCtx.fillStyle = '#fff';
  outputCtx.fillRect(0, 0, outputSize, outputSize);
  outputCtx.drawImage(
    rotatedCanvas,
    sourceX,
    sourceY,
    cropSize,
    cropSize,
    0,
    0,
    outputSize,
    outputSize
  );

  return outputCanvas;
};

const AdminPage: React.FC<AdminPageProps> = ({ session }) => {
  const navigate = useNavigate();
  const cropTouchZoneRef = useRef<HTMLDivElement | null>(null);
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef({
    mode: 'none' as 'none' | 'pan' | 'pinch',
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    startZoom: 1,
    startDistance: 0,
    startMidX: 0,
    startMidY: 0
  });
  
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this participant?')) {
      await deleteParticipant(session, id);
      fetchParticipants(tab); // Re-fetch current tab participants after deletion
    }
  };
  const [authenticated, setAuthenticated] = useState(() => {
    return localStorage.getItem(`adminAuthenticated_${session}`) === 'true';
  });
  const [inputPassword, setInputPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [name, setName] = useState('');
  const [speechTitle, setSpeechTitle] = useState('');
  const [role, setRole] = useState('Table Topic');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffsetX, setCropOffsetX] = useState(0);
  const [cropOffsetY, setCropOffsetY] = useState(0);
  const [message, setMessage] = useState('');
  const [participants, setParticipants] = useState<any[]>([]);
  const [tab, setTab] = useState('Table Topic');
  // Track if user manually selected the 'Overall' tab
  const [manualTab, setManualTab] = useState(false);
  const [currentRole, setCurrentRole] = useState('Table Topic');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showVotes, setShowVotes] = useState(false);
  const [voteStats, setVoteStats] = useState({ totalVotes: 0, maxVotes: 0, tieCount: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch current stage when component loads
  useEffect(() => {
    const fetchCurrentStage = async () => {
      try {
        const response = await getCurrentStage(session);
        if (response.currentRole) {
          setCurrentRole(response.currentRole);
        }
      } catch (error) {
        console.error('Failed to fetch current stage:', error);
      }
    };
    
    if (authenticated) {
      fetchCurrentStage();
    }
  }, [session, authenticated]);

  // Get the next role based on current role
  const getNextRole = (currentRole: string) => {
    const roleIndex = ROLES.indexOf(currentRole);
    if (roleIndex === -1 || roleIndex === ROLES.length - 1) {
      return 'result'; // Indicates it's the last stage
    }
    return ROLES[roleIndex + 1];
  };

  // Check if current role is the last stage
  const isLastStage = (currentRole: string) => {
    return ROLES.indexOf(currentRole) === ROLES.length - 1;
  };

  // Get the previous role based on current role
  const getPreviousRole = (currentRole: string) => {
    const roleIndex = ROLES.indexOf(currentRole);
    if (roleIndex === -1 || roleIndex === 0) {
      return ROLES[ROLES.length - 1]; // Wrap around to last role
    }
    return ROLES[roleIndex - 1];
  };

  const calculateVoteStats = (participants: any[]) => {
    const voteCounts = participants.map(p => p.votes || 0);
    const totalVotes = voteCounts.reduce((sum, count) => sum + count, 0);
    const maxVotes = Math.max(...voteCounts);
    const maxVoteCount = voteCounts.filter(count => count === maxVotes).length;
    
    return {
      totalVotes,
      maxVotes,
      tieCount: maxVoteCount > 1 ? maxVoteCount : 0
    };
  };

  // Handle image file selection and preview
  const handleImageChange = async (file: File | null) => {
    setImage(file);
    setRotation(0); // Reset rotation when new image is selected
    setCropZoom(1);
    setCropOffsetX(0);
    setCropOffsetY(0);
    setCroppedPreview(null);
    
    if (file) {
      try {
        const preview = await createNormalizedPreview(file);
        setImagePreview(preview);
      } catch (error) {
        console.error('Failed to prepare image preview:', error);
        setImagePreview(await readFileAsDataUrl(file));
      }
    } else {
      setImagePreview(null);
    }
  };

  // Rotate image preview
  const rotateImage = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const resetCrop = () => {
    setCropZoom(1);
    setCropOffsetX(0);
    setCropOffsetY(0);
  };

  const startPanGesture = (point: { x: number; y: number }) => {
    gestureRef.current = {
      ...gestureRef.current,
      mode: 'pan',
      startX: point.x,
      startY: point.y,
      startOffsetX: cropOffsetX,
      startOffsetY: cropOffsetY,
      startZoom: cropZoom
    };
  };

  const startPinchGesture = () => {
    const points = Array.from(activePointersRef.current.values());
    if (points.length < 2) {
      return;
    }

    const [firstPoint, secondPoint] = points;
    const mid = getTouchMidpoint(firstPoint, secondPoint);

    gestureRef.current = {
      ...gestureRef.current,
      mode: 'pinch',
      startDistance: getTouchDistance(firstPoint, secondPoint),
      startZoom: cropZoom,
      startOffsetX: cropOffsetX,
      startOffsetY: cropOffsetY,
      startMidX: mid.x,
      startMidY: mid.y
    };
  };

  const handleCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (cropTouchZoneRef.current) {
      cropTouchZoneRef.current.setPointerCapture(event.pointerId);
    }

    if (activePointersRef.current.size >= 2) {
      startPinchGesture();
      return;
    }

    startPanGesture({ x: event.clientX, y: event.clientY });
  };

  const handleCropPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!activePointersRef.current.has(event.pointerId)) {
      return;
    }

    event.preventDefault();
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (gestureRef.current.mode === 'pinch' && activePointersRef.current.size >= 2) {
      const points = Array.from(activePointersRef.current.values());
      const [firstPoint, secondPoint] = points;
      const currentDistance = getTouchDistance(firstPoint, secondPoint);
      const currentMid = getTouchMidpoint(firstPoint, secondPoint);
      const baseDistance = Math.max(10, gestureRef.current.startDistance);
      const newZoom = clamp((gestureRef.current.startZoom * currentDistance) / baseDistance, 1, 3);

      const zoneRect = cropTouchZoneRef.current?.getBoundingClientRect();
      const zoneWidth = Math.max(1, zoneRect?.width || 240);
      const zoneHeight = Math.max(1, zoneRect?.height || 240);
      const panScale = 100 / Math.max(1, newZoom);
      const deltaPercentX = ((currentMid.x - gestureRef.current.startMidX) / zoneWidth) * panScale;
      const deltaPercentY = ((currentMid.y - gestureRef.current.startMidY) / zoneHeight) * panScale;

      setCropZoom(newZoom);
      // Invert pan mapping so image follows the finger drag direction.
      setCropOffsetX(clamp(gestureRef.current.startOffsetX - deltaPercentX, -100, 100));
      setCropOffsetY(clamp(gestureRef.current.startOffsetY - deltaPercentY, -100, 100));
      return;
    }

    if (gestureRef.current.mode === 'pan') {
      const zoneRect = cropTouchZoneRef.current?.getBoundingClientRect();
      const zoneWidth = Math.max(1, zoneRect?.width || 240);
      const zoneHeight = Math.max(1, zoneRect?.height || 240);
      const panScale = 110 / Math.max(1, cropZoom);
      const deltaPercentX = ((event.clientX - gestureRef.current.startX) / zoneWidth) * panScale;
      const deltaPercentY = ((event.clientY - gestureRef.current.startY) / zoneHeight) * panScale;

      // Invert pan mapping so image follows the finger drag direction.
      setCropOffsetX(clamp(gestureRef.current.startOffsetX - deltaPercentX, -100, 100));
      setCropOffsetY(clamp(gestureRef.current.startOffsetY - deltaPercentY, -100, 100));
    }
  };

  const handleCropPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    activePointersRef.current.delete(event.pointerId);

    if (cropTouchZoneRef.current) {
      try {
        cropTouchZoneRef.current.releasePointerCapture(event.pointerId);
      } catch {
        // ignore release failures when capture was not active
      }
    }

    if (activePointersRef.current.size >= 2) {
      startPinchGesture();
      return;
    }

    const remainingPoint = Array.from(activePointersRef.current.values())[0];
    if (remainingPoint) {
      startPanGesture(remainingPoint);
      return;
    }

    gestureRef.current = {
      ...gestureRef.current,
      mode: 'none'
    };
  };

  useEffect(() => {
    let isCancelled = false;

    const updateCroppedPreview = async () => {
      if (!imagePreview) {
        setCroppedPreview(null);
        return;
      }

      try {
        const previewCanvas = await renderSquareCanvas(
          imagePreview,
          rotation,
          cropZoom,
          cropOffsetX,
          cropOffsetY,
          320
        );

        if (!isCancelled) {
          setCroppedPreview(previewCanvas.toDataURL('image/jpeg', 0.9));
        }
      } catch (error) {
        console.error('Failed to generate cropped preview:', error);
        if (!isCancelled) {
          setCroppedPreview(imagePreview);
        }
      }
    };

    updateCroppedPreview();

    return () => {
      isCancelled = true;
    };
  }, [imagePreview, rotation, cropZoom, cropOffsetX, cropOffsetY]);

  // Convert rotated image to blob for upload
  const getRotatedImageBlob = (): Promise<Blob> => {
    return new Promise((resolve) => {
      if (!imagePreview) {
        // Fallback if preview is unavailable.
        resolve(image as Blob);
        return;
      }

      renderSquareCanvas(
        imagePreview,
        rotation,
        cropZoom,
        cropOffsetX,
        cropOffsetY,
        900
      )
        .then((canvas) => {
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
              return;
            }

            resolve(image as Blob);
          }, 'image/jpeg', 0.9);
        })
        .catch((error) => {
          console.error('Failed to prepare cropped upload image:', error);
          resolve(image as Blob);
        });
    });
  };

  const fetchParticipants = async (role: string) => {
    // Fetch only for the selected role
    const res = await getParticipants(session, role);
    setParticipants(res.data);
    
    // Calculate vote statistics
    const stats = calculateVoteStats(res.data);
    setVoteStats(stats);
  };

  useEffect(() => {
    fetchParticipants(tab);
  }, [session, tab]);

  useEffect(() => {
    setTab(currentRole); // Always sync tab to currentRole unless manually changed to Overall
  }, [currentRole]);

  // Switch tab automatically when stage changes, unless user selected 'Overall'
  useEffect(() => {
    if (!manualTab) {
      setTab(currentRole);
    }
  }, [currentRole, manualTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return setMessage('Please select an image');
    
    setIsSubmitting(true);
    setMessage('');
    
    const formData = new FormData();
    formData.append('name', name);
    formData.append('speechTitle', speechTitle);
    formData.append('role', currentRole);
    
    // Get the rotated image blob if rotation was applied
    const imageBlob = await getRotatedImageBlob();
    formData.append('image', imageBlob, image.name);
    
    try {
      await uploadParticipant(session, formData);
      setMessage('Participant uploaded successfully');
      setName('');
      setSpeechTitle('');
      setRole('Table Topic');
      setImage(null);
      setImagePreview(null);
      setCroppedPreview(null);
      setRotation(0);
      setCropZoom(1);
      setCropOffsetX(0);
      setCropOffsetY(0);
      fetchParticipants(tab); // Re-fetch current tab participants after upload
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Upload failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear message only when event session is cleared
  const handleClearSession = async () => {
    await clearSession(session);
    setMessage('');
    setImagePreview(null);
    setCroppedPreview(null);
    setRotation(0);
    setCropZoom(1);
    setCropOffsetX(0);
    setCropOffsetY(0);
    window.location.reload();
  };

  return (
    !authenticated ? (
      <Container disableGutters maxWidth={false} sx={{
        minHeight: '100vh',
        pt: { xs: 14, sm: 18 },
        px: { xs: 2, sm: 4 },
        bgcolor: '#f8f6f1',
        backgroundImage: 'url(/tile_background.png)',
        backgroundRepeat: 'repeat',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}>
        <Paper elevation={0} sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 460,
          textAlign: 'center',
          borderTop: '6px solid #772432',
          borderRadius: 0,
          bgcolor: '#fff'
        }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, letterSpacing: 1, color: '#111', fontFamily: 'Montserrat, Arial, sans-serif' }}>
            Admin Login
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>Enter the session password to access admin panel.</Typography>
          <Box component="form" onSubmit={async (e) => {
            e.preventDefault();
            setIsAuthenticating(true);
            setAuthError('');
            
            try {
              const result = await verifySession(session, inputPassword);
              if (result.error) {
                setAuthError(result.error);
              } else {
                setAuthenticated(true);
                localStorage.setItem(`adminAuthenticated_${session}`, 'true');
                setInputPassword('');
              }
            } catch (error) {
              setAuthError('Failed to verify password. Please try again.');
            } finally {
              setIsAuthenticating(false);
            }
          }} sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <input
              type="password"
              value={inputPassword}
              onChange={e => setInputPassword(e.target.value)}
              placeholder="Session Password"
              style={{ padding: 12, fontSize: 18, width: '100%', maxWidth: 320, border: '1px solid #d7d0c6', borderRadius: 2, boxSizing: 'border-box' }}
              required
              disabled={isAuthenticating}
            />
            {authError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {authError}
              </Typography>
            )}
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={isAuthenticating}
              sx={{ minWidth: 140, py: 1.2, bgcolor: '#772432', '&:hover': { bgcolor: '#5f1c28' } }}
            >
              {isAuthenticating ? 'Verifying...' : 'Login'}
            </Button>
          </Box>
        </Paper>
      </Container>
    ) : (
      
            <Container disableGutters sx={{ minHeight: '100vh', position: 'relative', bgcolor: '#f8f6f1', backgroundImage: 'url(/tile_background.png)', backgroundRepeat: 'repeat' }}>
        <Box sx={{ 
          mb: { xs: 1, sm: 2 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 2, sm: 4 },
          position: 'relative',
          width: '100%'
        }}>
          <Typography variant="h4" align="left" sx={{ position: 'relative', zIndex: 1, fontWeight: 250, color: '#111', letterSpacing: 1, fontSize: { xs: '2.3rem', sm: '2.8rem' }, lineHeight: 1.1, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 4 } }}>
            Admin: {currentRole}
          </Typography>
          {authenticated && (
            <Box sx={{
              px: { xs: 3, sm: 4 },
              mt: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => navigate(`/${session}`)}
                sx={{ bgcolor: '#fff', textTransform: 'none' }}
              >
                Back to Voting
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => {
                  setAuthenticated(false);
                  localStorage.removeItem(`adminAuthenticated_${session}`);
                }}
                sx={{ bgcolor: '#fff' }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Box>
        <Container disableGutters maxWidth={false} sx={{
          width: '100%',
          pt: { xs: 2, sm: 2 },
          pb: { xs: 16, sm: 0 },
          boxShadow: 0,
          minHeight: 'calc(100vh - 120px)',
          mb: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          zIndex: 1
        }}>
          <Box sx={{ px: { xs: 2, sm: 4 } }}>
            
            {/* Current role indicator */}
            <Box sx={{ 
              mb: 2, 
              p: 1.5, 
              bgcolor: '#fff', 
              borderRadius: 1, 
              textAlign: 'center',
              border: '1px solid #e4ddd4'
            }}>
              <Typography variant="body1" sx={{ color: '#666', fontWeight: 500, mb: 0.5 }}>
                Current Role: {currentRole}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontWeight: 400 }}>
                Total Votes: {voteStats.totalVotes} | Highest: {voteStats.maxVotes}
                {voteStats.tieCount > 1 && (
                  <span style={{ color: '#f57c00', fontWeight: 600, marginLeft: '8px' }}>
                    ⚠️ TIE ({voteStats.tieCount} participants)
                  </span>
                )}
              </Typography>
            </Box>
            
            {/* Advance button moved below upload */}
            <Box sx={{ width: '100%' }}>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
                sx={{ py: 1.2, bgcolor: '#772432', '&:hover': { bgcolor: '#5f1c28' } }} 
                onClick={async () => {
                  const res = await nextStage(session);
                  if (res.currentRole !== undefined) {
                    setCurrentRole(res.currentRole || 'Table Topic');
                    // If advancing to result (last stage), navigate to results page
                    if (isLastStage(currentRole)) {
                      navigate(`/${session}/results`);
                    }
                  }
                }}
              >
                {isLastStage(currentRole) ? 'Proceed to Result' : `Advance session to ${getNextRole(currentRole)} >`}
              </Button>
            </Box>
            
            {/* Stage control buttons below advance */}
            <Box sx={{ 
              width: '100%', 
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}>
              <Button 
                variant="outlined" 
                color="secondary" 
                size="small" 
                sx={{ 
                  flex: 1,
                  py: 0.9,
                  borderColor: '#772432',
                  color: '#772432'
                }} 
                onClick={async () => {
                  const res = await prevStage(session);
                  if (res.currentRole !== undefined) setCurrentRole(res.currentRole || 'Table Topic');
                }}
              >
                {`< Back to ${getPreviousRole(currentRole)}`}
              </Button>
            </Box>
            
            <Divider sx={{ my: 3 }} />

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" required style={{ flex: 1, padding: 8, fontSize: 16 }} />
              </Box>
              
              {/* Speech Title Input */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <input 
                  value={speechTitle} 
                  onChange={e => setSpeechTitle(e.target.value)} 
                  placeholder="Speech Title (Optional)" 
                  style={{ 
                    flex: 1, 
                    padding: 8, 
                    fontSize: 16,
                    fontStyle: speechTitle ? 'normal' : 'italic',
                    color: speechTitle ? '#333' : '#999'
                  }} 
                />
              </Box>
              
              {/* Image Upload Section */}
              <Box sx={{ width: '100%' }}>
                <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => handleImageChange(e.target.files?.[0] || null)}
                    required
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                    id="file-input"
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    htmlFor="file-input"
                    sx={{
                      width: '100%',
                      py: 1.5,
                      border: '2px dashed #ccc',
                      borderColor: image ? '#1976d2' : '#ccc',
                      color: image ? '#1976d2' : '#666',
                      fontSize: '1rem',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    {image ? `Selected: ${image.name}` : '📷 Choose Image File'}
                  </Button>
                </Box>

                {/* Image Preview and Rotation Controls */}
                {imagePreview && (
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 2, 
                    p: 2, 
                    bgcolor: '#f9f9f9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
                      Square Crop Preview
                    </Typography>
                    
                    <Box
                      ref={cropTouchZoneRef}
                      onPointerDown={handleCropPointerDown}
                      onPointerMove={handleCropPointerMove}
                      onPointerUp={handleCropPointerUp}
                      onPointerCancel={handleCropPointerUp}
                      sx={{ 
                      width: { xs: 220, sm: 240 }, 
                      height: { xs: 220, sm: 240 }, 
                      border: '2px solid #e0e0e0',
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fff',
                      touchAction: 'none',
                      userSelect: 'none',
                      cursor: { xs: 'grab', sm: 'move' }
                    }}>
                      <img
                        src={croppedPreview || imagePreview}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ color: '#666', textAlign: 'center' }}>
                      Use one finger to pan. Use two fingers to pinch and zoom.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={rotateImage}
                        sx={{ 
                          minWidth: 120,
                          fontSize: '0.9rem'
                        }}
                      >
                        🔄 Rotate 90°
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={resetCrop}
                        sx={{ textTransform: 'none' }}
                      >
                        Reset Crop
                      </Button>
                      <Typography variant="body2" sx={{ color: '#666', ml: 1 }}>
                        {rotation}°
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isSubmitting}
                sx={{ 
                  position: 'relative',
                  minHeight: '48px'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 16,
                          height: 16,
                          border: '2px solid transparent',
                          borderTop: '2px solid currentColor',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                      Processing...
                    </Box>
                    <span style={{ visibility: 'hidden' }}>Upload and Submit</span>
                  </>
                ) : (
                  'Upload and Submit'
                )}
              </Button>
            </Box>
            {message && <Typography color="secondary" align="center">{message}</Typography>}
            
            <Divider sx={{ mb: 3 }} />
            
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setManualTab(false);
                fetchParticipants(v);
              }}
              centered
            >
              {ROLES.map(role => <Tab key={role} label={role} value={role} />)}
            </Tabs>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowVotes(!showVotes)}
              sx={{ 
                minWidth: 100,
                fontSize: '0.8rem',
                textTransform: 'none'
              }}
            >
              {showVotes ? 'Hide Votes' : 'Show Votes'}
            </Button>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 4 } }}>
            {ROLES.map(role => (
              tab === role && (
                <Grid container spacing={2} justifyContent="center" alignItems="center" columnSpacing={0} key={role}>
                  {participants.map(p => (
                    <Grid key={p._id}>
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          py: 0,
                          px: 0,
                          borderRadius: 2,
                          width: 'fit-content',
                          maxWidth: 400,
                          border: '1.0px solid #bbbbbb',
                          background:  '#fff',
                          transition: 'border 0.2s',
                          mx: 1,
                          overflow: 'hidden',
                          boxShadow: 0
                        }}
                      >
                        {p.imageUrl && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            py: 2,
                            px: 2
                          }}>
                            <CardMedia
                              component="img"
                              image={`${process.env.REACT_APP_API_URL}/api/${session}/image/${p.imageUrl.split('/').pop()}`}
                              alt={p.name}
                                                          sx={{
                              objectFit: 'cover',
                              width: { xs: 120, sm: 140 },
                              height: { xs: 120, sm: 140 },
                              borderRadius: '50%',
                              border: '2px solid #e0e0e0',
                              background: '#fff',
                              transition: 'all 0.2s',
                              display: 'block',
                              boxShadow: 2,
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: 4
                              }
                            }}
                            />
                          </Box>
                        )}
                        <Box sx={{ textAlign: 'left', px: 0, py: 1, width: '100%' }}>
                          <Typography variant="h6" sx={{ fontWeight: 400, fontSize: { xs: '1rem', sm: '1.15rem' }, color: '#333', mb: 0, px: 2 }}>{p.name}</Typography>
                          {showVotes && (
                            <Typography variant="body2" sx={{ px: 2, color: '#666' }}>Votes: {p.votes} / Total: {p.totalVotes}</Typography>
                          )}
                          <Button variant="outlined" color="error" size="small" sx={{ mt: 1, mx: 2 }} onClick={() => handleDelete(p._id)}>Delete</Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )
            ))}
          </Box>

          <Box sx={{
            mx: { xs: 2, sm: 4 },
            mt: 4,
            pt: 2,
            borderTop: '1px solid #e4ddd4',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Typography variant="caption" sx={{ color: '#8a6d3b' }}>
              Reset all participants, votes, and stage progress.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setConfirmOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: { xs: '100%', sm: 190 },
                py: 0.9
              }}
            >
              Reset Event Session
            </Button>
          </Box>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Reset Event Session</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to clear all event data and reset all stages? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} color="primary" size="small">Cancel</Button>
              <Button onClick={handleClearSession} color="error" size="small" autoFocus>Confirm</Button>
            </DialogActions>
          </Dialog>

        </Container>
      </Container>
    )
  );
};

export default AdminPage;
